import { publicEnv } from '@/lib/config/public-env';
import { getLogRocketRouting } from './deploy-environment';

/**
 * Type definition for valid environment names in the application.
 * This ensures type safety when working with environment variables.
 *
 * @deprecated For anything observability-related, use `DeployEnvironment` from
 * `./deploy-environment` instead. This three-value type is derived from
 * `process.env.ENVIRONMENT`, which CI never sets — see the note on
 * `getEnvironment()` below and docs/pdlc/observability.md.
 */
export type Environment = 'production' | 'test' | 'development';

/**
 * Configuration interface that defines all environment-specific settings.
 * This provides type safety and documentation for environment configuration.
 */
interface EnvironmentConfig {
    /** Current environment name */
    environment: Environment;
    /** Application version from package.json */
    version: string;
    /** Flag indicating if running in production */
    isProduction: boolean;
    /** Flag indicating if running in test environment */
    isTest: boolean;
    /** Flag indicating if running in development */
    isDevelopment: boolean;
    /** Unique identifier for this build */
    buildId: string;
    /** Domain name for the current environment */
    hostname: string;
    /** Base URL for API requests */
    apiEndpoint: string;
    /** Flag indicating if LogRocket should be enabled */
    enableLogRocket: boolean;
}

// Safe way to check if we're in a browser
const isBrowser = typeof window !== 'undefined';

/**
 * Determines the current environment based on NODE_ENV.
 * NODE_ENV is typically set during the Docker build process.
 *
 * ⚠️ This is very likely wrong in deployed builds. `next.config.mjs` inlines
 * `ENVIRONMENT: process.env.ENVIRONMENT || 'development'` at build time, and
 * the one build that every environment deploys (`verify-build` in
 * `.circleci/config.yml`) never sets `ENVIRONMENT` — so every deployed
 * build believes it is development. M4S-5593 moved LogRocket off this
 * function for exactly that reason; the remaining consumers (`logRouteVisit`,
 * `SessionDebug`, `getHostname`, `getApiEndpoint`) are a filed follow-up.
 *
 * @deprecated Use `resolveDeployEnvironment` from `./deploy-environment`.
 * @returns The current environment type
 */
function getEnvironment(): Environment {
    return (process.env.ENVIRONMENT as Environment) || 'development';
}

/**
 * Retrieves the unique identifier for the current build.
 *
 * CI sets this to the commit SHA — `NEXT_PUBLIC_BUILD_ID` in the generated
 * `.env.production`, and `BUILD_ID` via the `env` block in `next.config.mjs`.
 * Both are inlined into the client bundle. This used to return a hardcoded
 * '1.0.0', which is why the `buildId` trait looked two years stale (M4S-5593):
 * the plumbing was already here and simply was not read.
 *
 * @returns The build identifier string
 */
function getBuildId(): string {
    return process.env.NEXT_PUBLIC_BUILD_ID || process.env.BUILD_ID || 'unknown';
}

/**
 * Determines the appropriate hostname based on the current environment.
 *
 * @returns The hostname for the current environment
 */
function getHostname(): string {
    const env = getEnvironment();
    if (env === 'development') return 'localhost';
    return publicEnv('NEXT_PUBLIC_COOKIE_DOMAIN') || 'mfourstudio.com';
}

/**
 * Determines the API endpoint URL based on the current environment.
 *
 * @returns The API endpoint URL
 */
function getApiEndpoint(): string {
    return publicEnv('NEXT_PUBLIC_PORTAL_API_BASE_ENDPOINT') || 'http://localhost:3000';
}

/**
 * Determines if LogRocket should be enabled based on the NEXT_PUBLIC_ENABLE_LOGROCKET environment variable.
 *
 * @returns True if LogRocket should be enabled, false otherwise
 */
function shouldEnableLogRocket(): boolean {
    return publicEnv('NEXT_PUBLIC_ENABLE_LOGROCKET') === 'true';
}

/**
 * Main environment configuration object.
 * This is a singleton that provides access to all environment-specific settings.
 */
export const ENV: EnvironmentConfig = {
    environment: getEnvironment(),
    version: '1.0.0',
    isProduction: getEnvironment() === 'production',
    isTest: getEnvironment() === 'test',
    isDevelopment: getEnvironment() === 'development',
    buildId: getBuildId(),
    hostname: getHostname(),
    apiEndpoint: getApiEndpoint(),
    enableLogRocket: shouldEnableLogRocket(),
};

/**
 * Type guard to verify if a string is a valid environment name.
 *
 * @param env - The string to check
 * @returns True if the string is a valid environment name
 */
export function isValidEnvironment(env: string | undefined): env is Environment {
    return env === 'production' || env === 'test' || env === 'development';
}

/**
 * Returns this deployment's LogRocket app ID, or `null` when none is configured.
 *
 * Thin adapter over the pure resolver in `./deploy-environment`, which is where
 * the routing rules and their tests live. Each deployment names its project in
 * `NEXT_PUBLIC_LOGROCKET_PROJECT`; where that is unset there is no project, and
 * LogRocket must not start (M4S-5593).
 *
 * @returns The LogRocket application identifier, or `null` when unset
 */
export function getLogRocketAppId(): string | null {
    return getLogRocketRouting().appId;
}

/**
 * Safely retrieves environment variables with a fallback value.
 *
 * @param key - The environment variable name to retrieve
 * @param fallback - Default value if environment variable is not set
 * @returns The environment variable value or fallback
 */
export function getEnvVar(key: string, fallback: string = ''): string {
    // In the browser, we can only access NEXT_PUBLIC_ variables
    if (isBrowser) {
        const value = publicEnv(`NEXT_PUBLIC_${key}`);
        return value || fallback;
    }

    // On the server, we can access all environment variables
    const value = process.env[key];
    if (typeof value === 'undefined') {
        if (ENV.isDevelopment) {
            console.warn(`Environment variable ${key} is not defined, using fallback: ${fallback}`);
        }
        return fallback;
    }
    return value;
}
